import {
  isCumulative,
  isImpairment,
  isLifeSustainingTherapy
} from "data/constants/eligibilityQuestionCategories"

export enum EligibilityOutcome {
  Qualified = "QUALIFIED",
  SoftExit = "SOFT_EXIT"
}

export enum EligibilityRoute {
  Payment = "PAYMENT",
  SoftExit = "SOFT_EXIT"
}

export interface EligibilityAnswer {
  type: string
  answer: string
}

export interface EligibilityEvaluation {
  // Whether the Cumulative Effects (Q10) question should be presented.
  showQ10: boolean
  // Final outcome, or null while Q10 still needs to be answered.
  outcome: EligibilityOutcome | null
  // Where to route, or null while undetermined.
  routeTo: EligibilityRoute | null
  // True when qualification came from a "Yes" on the cumulative question.
  cumulativeCaseFlag: boolean
  impairmentYesCount: number
  impairmentYesExclLST: number
}

const isYes = (answer: string): boolean =>
  typeof answer === "string" && answer.toLowerCase() === "yes"

/**
 * Pure decision function for the reworded eligibility flow (Phase 1).
 *
 * Can be called after the impairment questions (to learn `showQ10`) and again
 * after Q10 is answered (to learn the final `outcome`/`routeTo`).
 */
export const evaluateEligibility = (
  answers: EligibilityAnswer[]
): EligibilityEvaluation => {
  const impairmentAnswers = answers.filter((a) => isImpairment(a.type))

  const impairmentYesCount = impairmentAnswers.filter((a) =>
    isYes(a.answer)
  ).length

  const impairmentYesExclLST = impairmentAnswers.filter(
    (a) => isYes(a.answer) && !isLifeSustainingTherapy(a.type)
  ).length

  const cumulativeAnswer = answers.find((a) => isCumulative(a.type))

  // 2+ qualifying impairments (excluding LST): qualified outright, skip Q10.
  if (impairmentYesExclLST >= 2) {
    return {
      showQ10: false,
      outcome: EligibilityOutcome.Qualified,
      routeTo: EligibilityRoute.Payment,
      cumulativeCaseFlag: false,
      impairmentYesCount,
      impairmentYesExclLST
    }
  }

  // Otherwise Q10 is in play (all impairments No, or exactly one qualifying Yes).
  if (!cumulativeAnswer) {
    return {
      showQ10: true,
      outcome: null,
      routeTo: null,
      cumulativeCaseFlag: false,
      impairmentYesCount,
      impairmentYesExclLST
    }
  }

  // Q10 = Yes -> cumulative-effects case, qualified.
  if (isYes(cumulativeAnswer.answer)) {
    return {
      showQ10: true,
      outcome: EligibilityOutcome.Qualified,
      routeTo: EligibilityRoute.Payment,
      cumulativeCaseFlag: true,
      impairmentYesCount,
      impairmentYesExclLST
    }
  }

  // Q10 = No but at least one impairment Yes -> still qualified.
  if (impairmentYesCount >= 1) {
    return {
      showQ10: true,
      outcome: EligibilityOutcome.Qualified,
      routeTo: EligibilityRoute.Payment,
      cumulativeCaseFlag: false,
      impairmentYesCount,
      impairmentYesExclLST
    }
  }

  // Q10 = No and all impairments No -> soft exit (never payment).
  return {
    showQ10: true,
    outcome: EligibilityOutcome.SoftExit,
    routeTo: EligibilityRoute.SoftExit,
    cumulativeCaseFlag: false,
    impairmentYesCount,
    impairmentYesExclLST
  }
}

export default evaluateEligibility
